"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
const THUNDER_URL = 'http://127.0.0.1:9998/jsonrpc';
const THUNDER_METHODS = {
  GET_PRIMARY_INTERFACE: 'org.rdk.NetworkManager.1.GetPrimaryInterface',
  GET_CONNECTED_SSID: 'org.rdk.NetworkManager.1.GetConnectedSSID',
  GET_AVAILABLE_INTERFACES: 'org.rdk.NetworkManager.1.GetAvailableInterfaces',
  GET_CORE_TEMPERATURE: 'org.rdk.System.1.getCoreTemperature',
  GET_RFC_CONFIG: 'org.rdk.System.1.getRFCConfig',
  GET_DEVICE_INFO: 'org.rdk.System.1.getDeviceInfo'
};

/* global fetch, window, URLSearchParams, AbortController */

// Reads the xumo_* params injected by the xumo-wrapper into the player URL.
// Returns null for each field that is absent.
function _readWrapperParams() {
  if (typeof window === 'undefined' || !window.location) return {};
  const params = new URLSearchParams(window.location.search);
  const connectionType = params.get('xumo_connection_type'); // 'wifi' | 'wired'
  const hasLinkRaw = params.get('xumo_has_link'); // 'true' | 'false'
  const hasLink = hasLinkRaw !== null ? JSON.parse(hasLinkRaw) : null;
  const deviceId = params.get('xumo_device_id');
  const deviceName = params.get('xumo_device_name');
  return {
    connection_type: connectionType,
    has_link: hasLink,
    device_id: deviceId,
    device_name: deviceName
  };
}
async function _thunder(method, params = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  const res = await fetch(THUNDER_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      jsonrpc: '2.0',
      id: 1,
      method,
      params
    }),
    signal: controller.signal
  }).finally(() => clearTimeout(timeoutId));
  if (!res.ok) throw new Error(`Thunder HTTP error: ${res.status}`);
  const data = await res.json();
  return data.result;
}
async function getConnectionType(wrapper = _readWrapperParams()) {
  if (wrapper.connection_type !== null && wrapper.connection_type !== undefined) {
    return wrapper.connection_type;
  }
  try {
    const result = await _thunder(THUNDER_METHODS.GET_PRIMARY_INTERFACE);
    return result.interface?.startsWith('eth') ? 'wired' : 'wifi';
  } catch (err) {
    console.warn('[XumoNativeService] getConnectionType unavailable:', err);
    return null;
  }
}
async function getNetworkMetrics(connectionType, wrapper = _readWrapperParams()) {
  const source = wrapper.connection_type !== null && wrapper.connection_type !== undefined ? 'wrapper' : 'thunder';
  try {
    if (connectionType === 'wifi') {
      if (source === 'wrapper') {
        // Firebolt doesn't expose SSID or signal strength — these remain null.
        // Thunder is also unavailable in this context.
        return {
          has_link: null,
          essid: null,
          signal_strength_dbm: null,
          frequency_mhz: null
        };
      }
      const result = await _thunder(THUNDER_METHODS.GET_CONNECTED_SSID);
      return {
        has_link: null,
        essid: result.ssid ?? null,
        signal_strength_dbm: result.signalStrength ?? null,
        frequency_mhz: result.frequency ?? null
      };
    }

    // wired path
    if (source === 'wrapper') {
      return {
        // null distinguishes "interface not found" from "interface found but disconnected"
        has_link: wrapper.has_link
      };
    }
    const result = await _thunder(THUNDER_METHODS.GET_AVAILABLE_INTERFACES);
    const eth = (result.interfaces ?? []).find(i => i.type === 'ETHERNET');
    return {
      has_link: eth ? eth.connected : null
    };
  } catch (err) {
    console.warn('[XumoNativeService] getNetworkMetrics unavailable:', err);
    return null;
  }
}
async function getSystemMetrics() {
  const [tempResult, memResult] = await Promise.allSettled([_thunder(THUNDER_METHODS.GET_CORE_TEMPERATURE), _thunder(THUNDER_METHODS.GET_RFC_CONFIG, {
    rfcList: ['Device.DeviceInfo.MemoryStatus.Total', 'Device.DeviceInfo.MemoryStatus.Free']
  })]);
  let temperatureCelsius = null;
  if (tempResult.status === 'fulfilled') {
    temperatureCelsius = tempResult.value?.temperature ?? null;
  } else {
    console.warn('[XumoNativeService] getCoreTemperature unavailable:', tempResult.reason);
  }
  let memoryTotalBytes = null;
  let memoryFreeBytes = null;
  if (memResult.status === 'fulfilled') {
    const rfc = memResult.value?.RFCConfig ?? {};
    const totalKb = parseInt(rfc['Device.DeviceInfo.MemoryStatus.Total'], 10);
    const freeKb = parseInt(rfc['Device.DeviceInfo.MemoryStatus.Free'], 10);
    if (!isNaN(totalKb) && !isNaN(freeKb)) {
      memoryTotalBytes = totalKb * 1024;
      memoryFreeBytes = freeKb * 1024;
    }
  } else {
    console.warn('[XumoNativeService] getRFCConfig (memory) unavailable:', memResult.reason);
  }
  return {
    temperature_celsius: temperatureCelsius,
    memory_total_bytes: memoryTotalBytes,
    memory_free_bytes: memoryFreeBytes,
    // /proc/loadavg and /proc/uptime require a native Thunder plugin not yet available.
    // These fields return null until the plugin is registered on the Xumo build.
    cpu_load_1m: null,
    cpu_load_5m: null,
    cpu_load_15m: null,
    device_uptime: null
  };
}
async function getDeviceName(wrapper = _readWrapperParams()) {
  if (wrapper.device_name) {
    return wrapper.device_name;
  }
  try {
    const result = await _thunder(THUNDER_METHODS.GET_DEVICE_INFO, {
      params: ['model_number']
    });
    const name = result.model_number ?? null;
    if (!name) {
      console.warn('[XumoNativeService] getDeviceName: model_number absent — key name may differ on this hardware SKU');
    }
    return name;
  } catch (err) {
    console.warn('[XumoNativeService] getDeviceName unavailable:', err);
    return null;
  }
}
async function getDeviceId(wrapper = _readWrapperParams()) {
  if (wrapper.device_id) {
    return wrapper.device_id;
  }
  try {
    const result = await _thunder(THUNDER_METHODS.GET_DEVICE_INFO, {
      params: ['estb_mac']
    });
    return result.estb_mac ?? null;
  } catch (err) {
    console.warn('[XumoNativeService] getDeviceId unavailable:', err);
    return null;
  }
}
async function collectAll() {
  try {
    const wrapper = _readWrapperParams();
    const connectionType = await getConnectionType(wrapper);
    const [networkMetrics, systemMetrics, deviceName, deviceId] = await Promise.allSettled([getNetworkMetrics(connectionType, wrapper), getSystemMetrics(), getDeviceName(wrapper), getDeviceId(wrapper)]);
    const network = networkMetrics.status === 'fulfilled' ? networkMetrics.value ?? {} : {};
    const system = systemMetrics.status === 'fulfilled' ? systemMetrics.value ?? {} : {};
    return {
      device_id: deviceId.status === 'fulfilled' ? deviceId.value : null,
      device_model: deviceName.status === 'fulfilled' ? deviceName.value : null,
      connection_type: connectionType,
      has_link: network.has_link ?? null,
      essid: network.essid ?? null,
      signal_strength_dbm: network.signal_strength_dbm ?? null,
      frequency_mhz: network.frequency_mhz ?? null,
      temperature_celsius: system.temperature_celsius ?? null,
      memory_total_bytes: system.memory_total_bytes ?? null,
      memory_free_bytes: system.memory_free_bytes ?? null,
      cpu_load_1m: system.cpu_load_1m ?? null,
      cpu_load_5m: system.cpu_load_5m ?? null,
      cpu_load_15m: system.cpu_load_15m ?? null,
      device_uptime: system.device_uptime ?? null
    };
  } catch (err) {
    console.warn('[XumoNativeService] collectAll failed:', err);
    return null;
  }
}
var _default = exports.default = {
  getDeviceId,
  getDeviceName,
  getConnectionType,
  getNetworkMetrics,
  getSystemMetrics,
  collectAll
};